import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { API, isEmbedded } from "@/lib/api";

const AuthContext = createContext(null);

// Wait for App Bridge (window.shopify) to be ready before the first auth call.
const waitForShopify = (ms = 4000) =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      if (window.shopify && typeof window.shopify.idToken === "function") return resolve(true);
      if (Date.now() - start >= ms) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });

// Resolve the merchant's shop domain from the URL or App Bridge config.
const getShopDomain = () => {
  try {
    const p = new URLSearchParams(window.location.search);
    const s = p.get("shop");
    if (s) return s;
    if (window.shopify && window.shopify.config && window.shopify.config.shop) {
      return window.shopify.config.shop;
    }
  } catch {
    /* ignore */
  }
  return null;
};

// When embedded but not yet provisioned, break out of the iframe and run OAuth.
// A short-lived sessionStorage guard prevents redirect loops.
const startOAuthInstall = () => {
  const shop = getShopDomain();
  if (!shop) return false;
  const key = "sl_oauth_started_at";
  const last = Number(sessionStorage.getItem(key) || 0);
  if (Date.now() - last < 20000) return false;
  sessionStorage.setItem(key, String(Date.now()));
  const url = `${API}/shopify/auth?shop=${encodeURIComponent(shop)}`;
  if (window.top === window.self) window.location.href = url;
  else window.open(url, "_top");
  return true;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setLoading(false);
    } catch {
      // Embedded + not provisioned → start OAuth (stay in loading state while redirecting).
      if (isEmbedded() && startOAuthInstall()) return;
      setUser(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (isEmbedded()) {
      // Ensure App Bridge is loaded so the session token can be attached.
      waitForShopify().then(checkAuth);
    } else {
      checkAuth();
    }
  }, [checkAuth]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
